import React from "react";
import Tile from "@src/components/containers/Tile";
import OrderStatuses from "@src/constants/OrderStatuses";
import moment from "moment";
import ProductImage from "@src/components/widgets/ProductImage";
import ChildCutoffContainer from "@src/components/containers/ChildCutoffContainer";
import { FlatProduct } from "@src/types/product";
import DistributorLogo from "@src/components/common/DistributorLogo";
import { Link } from "react-router-dom";

export interface Order {
  id: string;
  orderNumber: string;
  status: string;
  distributorSlug: string;
  products: FlatProduct[];
  backorderedProducts: FlatProduct[];
  timestamps: {
    placedAtTimestamp: string;
    shippedAtTimestamp?: string;
    deliveredAtTimestamp?: string;
    canceledAtTimestamp?: string;
    returnedAtTimestamp?: string;
    arrivesAtTimestamp?: string;
  };
}

interface OrderDetailTileProps {
  order: Order;
}

export default function OrderDetailTile({ order }: OrderDetailTileProps) {
  let orderStatusDetailsJsx = null;

  if (order.status === OrderStatuses.Delivered) {
    orderStatusDetailsJsx = (
      <span>
        Delivered{" "}
        {!!order.timestamps.deliveredAtTimestamp &&
          moment(order.timestamps.deliveredAtTimestamp).format("ddd, MMM D")}
      </span>
    );
  } else {
    orderStatusDetailsJsx = (
      <>
        <span>
          Placed{" "}
          {moment(order.timestamps.placedAtTimestamp).format("ddd, MMM D")}
        </span>

        {order.status === OrderStatuses.Shipped && (
          <>
            <span>|</span>
            <span className="text-success font-weight-bold">Shipped</span>
          </>
        )}

        {order.timestamps.arrivesAtTimestamp && (
          <>
            <span>|</span>
            <span>
              Arrives by{" "}
              {moment(order.timestamps.arrivesAtTimestamp).format("ddd, MMM D")}
            </span>
          </>
        )}
      </>
    );
  }

  return (
    <Link
      id={`order-detail-tile-link-${order.id}`}
      data-testid={`order-detail-tile-link-${order.id}`}
      to={`/orders/${order.orderNumber}`}
    >
      <Tile
        className="border-thick padding-y-xs"
        data-testid={`order-detail-tile-${order.id}`}
      >
        <div
          className="flex-row flex-center space-between margin-bottom-md border-bottom"
          data-testid={`order-detail-tile-header-${order.id}`}
        >
          <DistributorLogo
            distributorSlug={order.distributorSlug}
            height={36}
          />

          <div className="flex-row flex-center flex-end spaced-content text-secondary text-xs">
            <Link
              id={`order-detail-order-number-link-${order.id}`}
              data-testid={`order-detail-order-number-link-${order.id}`}
              to={`/orders/${order.orderNumber}`}
            >
              Order #{order.orderNumber}
            </Link>
            <span>|</span>

            {orderStatusDetailsJsx}
          </div>
        </div>

        <div
          className="flex-1"
          data-testid={`order-detail-tile-content-${order.id}`}
        >
          {order.products.length === 0 &&
            order.backorderedProducts.length === 0 && (
              <span className="text-secondary text-italic">
                No products linked to this order.
              </span>
            )}
          <ChildCutoffContainer childContainerClassName="flex-row spaced-content-lg">
            {order.products.map((product) => (
              <React.Fragment
                key={`order-${order.id}-dist-${order.distributorSlug}-product-${product.id}`}
              >
                <ProductImage imageSrc={product.thumbnailUrl} sizePx={60} />
              </React.Fragment>
            ))}
          </ChildCutoffContainer>
        </div>

        {/* <Link className="text-xs">Contact Rep</Link>
        <Link className="text-xs">Invoice #123456</Link>
        {(order.status === OrderStatuses.Pending || order.status === OrderStatuses.Shipped) && (
          <>
            <Link className="text-xs">Track Package</Link>
          </>
        )} */}
      </Tile>
    </Link>
  );
}
